import React from "react";
import { MetricKpi, MetricLabel } from "../../MetricCards";
import panelStyles from "../../styles/Panel.module.css";
import analyticsStyles from "../../styles/Analytics.module.css";
import {
  formatMoney,
  formatNumber,
  formatPercent,
  formatShare
} from "../../utils/formatters";

function row(title, hint, day, week, month) {
  return { title, hint, day, week, month };
}

export function AnalyticsMoneyBlock({ analyticsData, activeByPlan, day, week, month }) {
  const parentShare = (period) =>
    period?.revenue > 0
      ? formatPercent(((period?.parentPayment?.revenue || 0) / period.revenue) * 100)
      : formatPercent(null);

  const rows = [
    row("Выручка", "Сумма покупок (amount) за период.", formatMoney(day.revenue), formatMoney(week.revenue), formatMoney(month.revenue)),
    row("ARPPU (платные)", "Выручка (amount>0) / уникальные платящие пользователи (amount>0).", formatMoney(day.arppu), formatMoney(week.arppu), formatMoney(month.arppu)),
    row("ARPPU (все события purchase)", "Выручка / уникальные пользователи с любым purchase-событием (в т.ч. с amount=0).", formatMoney(day.arppuAll || 0), formatMoney(week.arppuAll || 0), formatMoney(month.arppuAll || 0)),
    row(
      "Оплата родителями",
      "purchase с payment_source=parent_payment (учитывается в выручке, но выделяется отдельно).",
      `${formatNumber(day.parentPayment?.users || 0)} / ${formatMoney(day.parentPayment?.revenue || 0)} (${parentShare(day)})`,
      `${formatNumber(week.parentPayment?.users || 0)} / ${formatMoney(week.parentPayment?.revenue || 0)} (${parentShare(week)})`,
      `${formatNumber(month.parentPayment?.users || 0)} / ${formatMoney(month.parentPayment?.revenue || 0)} (${parentShare(month)})`
    ),
    row("VK Donut", "Выручка с source=vkdonut.", formatMoney(day.bySource.vkdonut), formatMoney(week.bySource.vkdonut), formatMoney(month.bySource.vkdonut)),
    row("CloudPayments", "Выручка с source=cloudpayments.", formatMoney(day.bySource.cloudpayments || 0), formatMoney(week.bySource.cloudpayments || 0), formatMoney(month.bySource.cloudpayments || 0)),
    row("Точка", "Выручка с source=tochka.", formatMoney(day.bySource.tochka), formatMoney(week.bySource.tochka), formatMoney(month.bySource.tochka)),
    row("Unitpay", "Выручка с source=unitpay.", formatMoney(day.bySource.unitpay || 0), formatMoney(week.bySource.unitpay || 0), formatMoney(month.bySource.unitpay || 0)),
    row("ЮKassa (legacy)", "Выручка с source=yookassa (legacy).", formatMoney(day.bySource.yookassa || 0), formatMoney(week.bySource.yookassa || 0), formatMoney(month.bySource.yookassa || 0)),
    row(
      "Покупки по платежкам",
      "Количество purchase-событий по source за период.",
      `VK ${formatNumber(day.bySourcePurchases?.vkdonut || 0)}, CP ${formatNumber(day.bySourcePurchases?.cloudpayments || 0)}, Точка ${formatNumber(day.bySourcePurchases?.tochka || 0)}, Unitpay ${formatNumber(day.bySourcePurchases?.unitpay || 0)}, ЮK ${formatNumber(day.bySourcePurchases?.yookassa || 0)}, др ${formatNumber(day.bySourcePurchases?.other || 0)}`,
      `VK ${formatNumber(week.bySourcePurchases?.vkdonut || 0)}, CP ${formatNumber(week.bySourcePurchases?.cloudpayments || 0)}, Точка ${formatNumber(week.bySourcePurchases?.tochka || 0)}, Unitpay ${formatNumber(week.bySourcePurchases?.unitpay || 0)}, ЮK ${formatNumber(week.bySourcePurchases?.yookassa || 0)}, др ${formatNumber(week.bySourcePurchases?.other || 0)}`,
      `VK ${formatNumber(month.bySourcePurchases?.vkdonut || 0)}, CP ${formatNumber(month.bySourcePurchases?.cloudpayments || 0)}, Точка ${formatNumber(month.bySourcePurchases?.tochka || 0)}, Unitpay ${formatNumber(month.bySourcePurchases?.unitpay || 0)}, ЮK ${formatNumber(month.bySourcePurchases?.yookassa || 0)}, др ${formatNumber(month.bySourcePurchases?.other || 0)}`
    ),
    row("Продления", "Покупки с kind=renewal/prolonged.", formatNumber(day.renewals), formatNumber(week.renewals), formatNumber(month.renewals)),
    row(
      "Подписки (новые) — уник. / выручка",
      "Новые подписки.",
      `${formatNumber(day.subscriptions.new.users)} (${formatMoney(day.subscriptions.new.revenue)})`,
      `${formatNumber(week.subscriptions.new.users)} (${formatMoney(week.subscriptions.new.revenue)})`,
      `${formatNumber(month.subscriptions.new.users)} (${formatMoney(month.subscriptions.new.revenue)})`
    ),
    row(
      "Подписки (продления) — уник. / выручка",
      "Продления подписок.",
      `${formatNumber(day.subscriptions.renewals.users)} (${formatMoney(day.subscriptions.renewals.revenue)})`,
      `${formatNumber(week.subscriptions.renewals.users)} (${formatMoney(week.subscriptions.renewals.revenue)})`,
      `${formatNumber(month.subscriptions.renewals.users)} (${formatMoney(month.subscriptions.renewals.revenue)})`
    ),
    row(
      "Подписки (всего) — уник. / выручка",
      "Все подписки.",
      `${formatNumber(day.subscriptions.total.users)} (${formatMoney(day.subscriptions.total.revenue)})`,
      `${formatNumber(week.subscriptions.total.users)} (${formatMoney(week.subscriptions.total.revenue)})`,
      `${formatNumber(month.subscriptions.total.users)} (${formatMoney(month.subscriptions.total.revenue)})`
    ),
    row(
      "Разовые покупки — уник. / выручка",
      "Покупки без автосписания.",
      `${formatNumber(day.oneTime.users)} (${formatMoney(day.oneTime.revenue)})`,
      `${formatNumber(week.oneTime.users)} (${formatMoney(week.oneTime.revenue)})`,
      `${formatNumber(month.oneTime.users)} (${formatMoney(month.oneTime.revenue)})`
    ),
    row("Ошибки оплаты", "payment_fail.", formatNumber(day.paymentFails), formatNumber(week.paymentFails), formatNumber(month.paymentFails)),
    row(
      "Отмены подписок",
      "Только реальные отмены подписки без payment_fail.",
      formatNumber(day.subscriptionCancels),
      formatNumber(week.subscriptionCancels),
      formatNumber(month.subscriptionCancels)
    ),
    row("Маржа", "(Выручка - себестоимость токенов) / выручка.", formatPercent(day.margin), formatPercent(week.margin), formatPercent(month.margin))
  ];

  return (
    <div className={analyticsStyles.block}>
      <h3>Деньги</h3>
      <div className={panelStyles.tableWrap}>
        <table className={panelStyles.table}>
          <thead>
            <tr>
              <th>Метрика</th>
              <th>День</th>
              <th>Неделя</th>
              <th>Месяц</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => (
              <tr key={item.title}>
                <td>
                  <MetricLabel title={item.title} hint={item.hint} />
                </td>
                <td>{item.day}</td>
                <td>{item.week}</td>
                <td>{item.month}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={analyticsStyles.kpis}>
        <MetricKpi
          title="Новые платящие за месяц"
          value={formatNumber(analyticsData.money.newPayersMonth)}
          hint="Пользователи с первой покупкой за последние 30 дней."
        />
        <MetricKpi
          title="Среднее время до первой покупки"
          value={`${formatNumber(analyticsData.money.avgTimeToFirstPurchaseDays)} дн`}
          hint="Среднее дней от регистрации до первой покупки."
        />
        <MetricKpi
          title="Доля выручки от старых пользователей"
          value={formatPercent(analyticsData.money.revenueFromOldUsersShare)}
          hint="Выручка от пользователей, зарегистрированных до начала окна."
        />
      </div>

      <div className={analyticsStyles.kpis}>
        <MetricKpi
          title="Подписки: платные активные"
          value={formatNumber(analyticsData.money.activeSubscriptions?.paid ?? analyticsData.money.activePaid)}
          hint="Подтверждённые платные подписки (subscription_source: payment/vkdonut/cloudpayments/tochka)."
        />
        <MetricKpi
          title="Подписки: подарочные"
          value={formatNumber(analyticsData.money.activeSubscriptions?.gifted ?? 0)}
          hint="Бесплатные выдачи: ручные подарки, рулетка, промо и т.п. (не выручка)."
        />
        <MetricKpi
          title="Подписки: legacy (не KPI)"
          value={formatNumber(analyticsData.money.activeSubscriptions?.legacy ?? 0)}
          hint="Старые/неподтверждённые источники (unknown_legacy). Не учитываем в KPI, LTV и конверсии."
        />
        <MetricKpi
          title="Подписки: неизвестно"
          value={formatNumber(analyticsData.money.activeSubscriptions?.unknown ?? 0)}
          hint="Не удалось классифицировать источник. Нужно разметить subscription_source."
        />
      </div>

      <div className={analyticsStyles.kpis}>
        <MetricKpi
          title="Lite"
          value={`${formatNumber(activeByPlan.lite || month.byPlan.lite.count)} (${formatMoney(month.byPlan.lite.revenue)})`}
          hint="Активные подписки Lite / выручка по Lite за 30 дней."
        />
        <MetricKpi
          title="Pro"
          value={`${formatNumber(activeByPlan.pro || month.byPlan.pro.count)} (${formatMoney(month.byPlan.pro.revenue)})`}
          hint="Активные подписки Pro / выручка по Pro за 30 дней."
        />
        <MetricKpi
          title="Max"
          value={`${formatNumber(activeByPlan.max || month.byPlan.max.count)} (${formatMoney(month.byPlan.max.revenue)})`}
          hint="Активные подписки Max / выручка по Max за 30 дней."
        />
      </div>

      <div className={analyticsStyles.kpis}>
        <MetricKpi title="Доля Lite" value={formatShare(month.byPlan.lite.revenue, month.revenue)} hint="Выручка Lite / вся выручка месяца." />
        <MetricKpi title="Доля Pro" value={formatShare(month.byPlan.pro.revenue, month.revenue)} hint="Выручка Pro / вся выручка месяца." />
        <MetricKpi title="Доля Max" value={formatShare(month.byPlan.max.revenue, month.revenue)} hint="Выручка Max / вся выручка месяца." />
      </div>
    </div>
  );
}

export default AnalyticsMoneyBlock;
